import styled from 'styled-components'

export const FormPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.forecast.background1};
  min-width: 0;
`

export const FormHeader = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.background3};
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const FormTitle = styled.div`
  font-weight: 600;
  font-size: 0.9375rem;
`

export const FormContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
`

export const EmptyFormState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  opacity: 0.5;
  text-align: center;
  padding: 2rem;
  gap: 0.5rem;
`

export const EmptyStateIcon = styled.div`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
`

export const EmptyStateSubtext = styled.div`
  font-size: 0.75rem;
`

export const FormTable = styled.div`
  border: 1px solid ${({ theme }) => theme.forecast.background4};
  border-radius: 4px;
  overflow: hidden;
`

export const FormRow = styled.div<{ $noBorder?: boolean }>`
  display: flex;
  border-bottom: 1px solid
    ${({ theme, $noBorder }) =>
      $noBorder ? 'transparent' : theme.forecast.background3};

  &:last-child {
    border-bottom: none;
  }
`

export const FormCell = styled.div<{ $flex?: number; $noBorder?: boolean }>`
  flex: ${({ $flex = 1 }) => $flex};
  padding: 0.5rem;
  border-right: 1px solid ${({ theme }) => theme.forecast.background3};
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  ${({ $noBorder }) => $noBorder && 'border-right: none;'}

  &:last-child {
    border-right: none;
  }
`

export const FormCellNarrow = styled(FormCell)`
  flex: 0.5;
`

export const CellLabel = styled.div`
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.6;
`

export const CellInput = styled.input`
  background: ${({ theme }) => theme.forecast.background2};
  border: 1px solid transparent;
  border-radius: 3px;
  padding: 0.375rem 0.5rem;
  font-size: 0.8125rem;
  color: inherit;
  width: 100%;
  transition:
    border-color 0.15s,
    background 0.15s;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.forecast.background3};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.forecast.primary};
    background: ${({ theme }) => theme.forecast.background3};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &::placeholder {
    opacity: 0.4;
  }
`

export const CellTextarea = styled.textarea`
  background: ${({ theme }) => theme.forecast.background2};
  border: 1px solid transparent;
  border-radius: 3px;
  padding: 0.5rem;
  font-size: 0.8125rem;
  color: inherit;
  resize: none;
  font-family: inherit;
  flex: 1;
  min-height: 60px;
  transition:
    border-color 0.15s,
    background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.forecast.background3};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.forecast.primary};
    background: ${({ theme }) => theme.forecast.background3};
  }

  &::placeholder {
    opacity: 0.4;
  }
`

export const CellCheckbox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 0.25rem;
`

export const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: ${({ theme }) => theme.forecast.primary};
`

export const CheckboxSublabel = styled.span`
  font-size: 0.625rem;
  opacity: 0.5;
`

export const SplitRow = styled.div<{ $minHeight?: string }>`
  display: flex;
  min-height: ${({ $minHeight = '140px' }) => $minHeight};
  border-bottom: 1px solid ${({ theme }) => theme.forecast.background3};
`

export const SplitCell = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  border-right: 1px solid ${({ theme }) => theme.forecast.background3};

  &:last-child {
    border-right: none;
  }
`

export const SplitCellHeader = styled.div`
  padding: 0.5rem;
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.6;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.background3};
`

export const SplitCellContent = styled.div`
  flex: 1;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
`

export const SymbolButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`

export const SymbolButton = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  font-weight: 700;
  font-size: 1rem;
  color: ${({ $selected }) => ($selected ? '#ef4444' : 'inherit')};
  background: ${({ theme, $selected }) =>
    $selected ? 'rgba(239, 68, 68, 0.15)' : theme.forecast.background2};
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? '#ef4444' : theme.forecast.background3};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s;
  opacity: ${({ $selected }) => ($selected ? 1 : 0.6)};

  &:hover {
    opacity: 1;
    border-color: #ef4444;
    color: #ef4444;
  }
`

export const DateInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

export const ClearButton = styled.button`
  background: transparent;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  opacity: 0.4;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.15s;
  color: inherit;

  &:hover {
    opacity: 1;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`
